import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/adminlayout';
import { PRODUCTION_URL } from '../productionLink/productionLink';
import './guest-bills.css';

const GuestBills = () => {
    const { bookingId } = useParams();
    const navigate = useNavigate();
    const [booking, setBooking] = useState(null);
    const [bills, setBills] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [formData, setFormData] = useState({
        billType: 'food',
        description: '',
        amount: '',
        quantity: 1,
        gstPercentage: 5
    });

    const billTypes = [
        { value: 'food', label: 'Food & Dining' },
        { value: 'tip', label: 'Tips' },
        { value: 'laundry', label: 'Laundry' },
        { value: 'minibar', label: 'Minibar' },
        { value: 'room_service', label: 'Room Service' },
        { value: 'spa', label: 'Spa & Wellness' },
        { value: 'parking', label: 'Parking' },
        { value: 'telephone', label: 'Telephone' },
        { value: 'miscellaneous', label: 'Miscellaneous' }
    ];

    const fetchBills = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            // Fetch booking details
            const bookingRes = await fetch(`${PRODUCTION_URL}/api/admin/guest-card/${bookingId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const bookingData = await bookingRes.json();
            if (bookingData.success) {
                setBooking(bookingData.data.booking);
            }

            // Fetch bills
            const billsRes = await fetch(`${PRODUCTION_URL}/api/admin/guest-bills/${bookingId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const billsData = await billsRes.json();
            if (billsData.success) {
                setBills(billsData.data.bills);
                setSummary(billsData.data.summary);
            }
        } catch (err) {
            console.error('Error fetching bills:', err);
        } finally {
            setLoading(false);
        }
    }, [bookingId]);

    useEffect(() => {
        if (bookingId) {
            fetchBills();
        }
    }, [bookingId, fetchBills]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddBill = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/guest-bill`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    bookingId: parseInt(bookingId),
                    billType: formData.billType,
                    description: formData.description,
                    amount: parseFloat(formData.amount),
                    quantity: parseInt(formData.quantity),
                    gstPercentage: parseFloat(formData.gstPercentage),
                    billedBy: localStorage.getItem('username') || 'Admin'
                })
            });

            const data = await response.json();
            if (data.success) {
                setShowAddModal(false);
                setFormData({
                    billType: 'food',
                    description: '',
                    amount: '',
                    quantity: 1,
                    gstPercentage: 5
                });
                fetchBills();
            } else {
                alert(data.message || 'Failed to add bill');
            }
        } catch (err) {
            console.error('Error adding bill:', err);
            alert('Error adding bill');
        }
    };

    const handleDeleteBill = async (billId) => {
        if (!window.confirm('Are you sure you want to delete this bill?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/guest-bill/${billId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            if (data.success) {
                fetchBills();
            } else {
                alert(data.message || 'Failed to delete bill');
            }
        } catch (err) {
            console.error('Error deleting bill:', err);
        }
    };

    const handleMarkPaid = async (billId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${PRODUCTION_URL}/api/admin/guest-bill/${billId}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status: 'paid' })
            });

            if (response.ok) {
                fetchBills();
            }
        } catch (err) {
            console.error('Error updating bill:', err);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return <div className="bills-container"><div className="loading">Loading...</div></div>;
    }

    return (
        <AdminLayout>
            <div className="bills-container">
                <div className="bills-header">
                    <div className="header-info">
                        <button onClick={() => navigate(-1)} className="btn-back">← Back</button>
                        <div>
                            <h1>Guest Bills</h1>
                            {booking && (
                                <p className="guest-info">
                                    {booking.guest_name} | Room {booking.room_number}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowAddModal(true)}
                    >
                        + Add Charge
                    </button>
                </div>

                {/* Summary Cards */}
                {summary && (
                    <div className="summary-cards">
                        <div className="summary-card">
                            <span className="label">Total Bills</span>
                            <span className="value">{summary.totalBills}</span>
                        </div>
                        <div className="summary-card">
                            <span className="label">Total Amount</span>
                            <span className="value">{formatCurrency(summary.totalAmount)}</span>
                        </div>
                        <div className="summary-card success">
                            <span className="label">Paid</span>
                            <span className="value">{formatCurrency(summary.paidAmount)}</span>
                        </div>
                        <div className="summary-card warning">
                            <span className="label">Pending</span>
                            <span className="value">{formatCurrency(summary.pendingAmount)}</span>
                        </div>
                    </div>
                )}

                {/* Bills Table */}
                <div className="bills-table-container">
                    {bills.length === 0 ? (
                        <div className="no-bills">
                            <p>No additional charges added yet.</p>
                            <button
                                className="btn btn-primary"
                                onClick={() => setShowAddModal(true)}
                            >
                                Add First Charge
                            </button>
                        </div>
                    ) : (
                        <table className="bills-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Amount</th>
                                    <th>GST</th>
                                    <th>Total</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bills.map(bill => (
                                    <tr key={bill.id}>
                                        <td>{formatDate(bill.bill_date)}</td>
                                        <td className="bill-type-cell">
                                            <span className={`type-badge ${bill.bill_type}`}>
                                                {billTypes.find(t => t.value === bill.bill_type)?.label || bill.bill_type}
                                            </span>
                                        </td>
                                        <td>{bill.description || '-'}</td>
                                        <td>{bill.quantity}</td>
                                        <td>{formatCurrency(bill.amount)}</td>
                                        <td>{formatCurrency(bill.gst_amount)}</td>
                                        <td className="total-cell">{formatCurrency(bill.total_amount)}</td>
                                        <td>
                                            <span className={`status-badge ${bill.status}`}>
                                                {bill.status}
                                            </span>
                                        </td>
                                        <td className="actions-cell">
                                            {bill.status === 'pending' && (
                                                <>
                                                    <button
                                                        className="btn-small btn-success"
                                                        onClick={() => handleMarkPaid(bill.id)}
                                                    >
                                                        Mark Paid
                                                    </button>
                                                    <button
                                                        className="btn-small btn-danger"
                                                        onClick={() => handleDeleteBill(bill.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Add Bill Modal */}
                {showAddModal && (
                    <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                        <div className="modal" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Add Charge</h2>
                                <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
                            </div>
                            <form onSubmit={handleAddBill}>
                                <div className="form-group">
                                    <label>Charge Type</label>
                                    <select
                                        name="billType"
                                        value={formData.billType}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        {billTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Enter description..."
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Amount (₹)</label>
                                        <input
                                            type="number"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleInputChange}
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Quantity</label>
                                        <input
                                            type="number"
                                            name="quantity"
                                            value={formData.quantity}
                                            onChange={handleInputChange}
                                            min="1"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>GST %</label>
                                    <select
                                        name="gstPercentage"
                                        value={formData.gstPercentage}
                                        onChange={handleInputChange}
                                    >
                                        <option value="0">No GST (0%)</option>
                                        <option value="5">5% GST</option>
                                        <option value="12">12% GST</option>
                                        <option value="18">18% GST</option>
                                        <option value="28">28% GST</option>
                                    </select>
                                </div>
                                <div className="form-preview">
                                    <p>
                                        <strong>Preview:</strong> {formatCurrency(formData.amount * formData.quantity)} +
                                        {formatCurrency((formData.amount * formData.quantity * formData.gstPercentage) / 100)} GST =
                                        <strong> {formatCurrency(formData.amount * formData.quantity * (1 + formData.gstPercentage / 100))}</strong>
                                    </p>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        Add Charge
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default GuestBills;
